import React, { useEffect, useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stepper,
  Step,
  StepLabel,
  Box,
  Typography,
  TextField,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Grid,
  IconButton,
  Tooltip,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  Paper,
} from '@mui/material';
import { Notification } from '@progress/kendo-react-notification';
import SettingsIcon from '@mui/icons-material/Settings';
import LinkIcon from '@mui/icons-material/Link';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import { authService } from '../../..';
import { httpGetAsync, httpPostAsync } from '../../../utils/HttpClient';
import PdfCoordinatePicker from './PdfCoordinatePicker';

// E-Sign types - defined locally until react-shared-library is rebuilt
export enum ESignSignerRole {
  Buyer = 1,
  CoBuyer = 2,
  Dealer = 3,
  Employee = 4,
  Lender = 5,
  CoBuyer2 = 6,
  ThirdPartyOwner = 7
}

export const ESignSignerRoleOptions = [
  { value: ESignSignerRole.Buyer, label: "Buyer" },
  { value: ESignSignerRole.CoBuyer, label: "Co-Buyer" },
  { value: ESignSignerRole.Dealer, label: "Dealer" },
  { value: ESignSignerRole.Employee, label: "Employee" },
  { value: ESignSignerRole.Lender, label: "Lender" },
  { value: ESignSignerRole.CoBuyer2, label: "Co-Buyer 2" },
  { value: ESignSignerRole.ThirdPartyOwner, label: "Third Party Owner" }
];

export interface ESignSignerInput {
  role: ESignSignerRole;
  name: string;
  email: string;
}

export interface ZohoSignConfigurationStatus {
  isEnabled: boolean;
  isConfigured: boolean;
  isAuthenticated: boolean;
  authorizationUrl?: string;
}

export interface DocumentWithESignFields {
  loanDocumentId: string;
  name: string;
  description?: string;
  hasESignFields: boolean;
  fieldCount: number;
  signerRoles?: ESignSignerRole[];
}

export interface SendForSigningResult {
  success: boolean;
  requestId?: string;
  providerUrl?: string;
  errorMessage?: string;
}

export interface ZohoSignDialogProps {
  applicationId: string;
  applicationNumber?: string;
  documentIds: string[];
  buyerName?: string;
  buyerEmail?: string;
  coBuyerName?: string;
  coBuyerEmail?: string;
  employeeName?: string;
  employeeEmail?: string;
  onClose: () => void;
  onSuccess?: (requestId: string) => void;
}

const steps = ['Configure', 'Select Documents', 'Add Signers', 'Review & Send'];

export default function ZohoSignDialog(props: ZohoSignDialogProps) {
  const {
    applicationId,
    applicationNumber,
    documentIds,
    buyerName,
    buyerEmail,
    coBuyerName,
    coBuyerEmail,
    employeeName,
    employeeEmail,
    onClose,
    onSuccess,
  } = props;

  // State
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Configuration state
  const [configStatus, setConfigStatus] = useState<ZohoSignConfigurationStatus | null>(null);
  
  // Document selection state
  const [documents, setDocuments] = useState<DocumentWithESignFields[]>([]);
  const [selectedDocumentIds, setSelectedDocumentIds] = useState<string[]>([]);
  
  // Signer state
  const [signers, setSigners] = useState<ESignSignerInput[]>([]);
  
  // Email details
  const [subject, setSubject] = useState(applicationNumber ? `#${applicationNumber} Documents` : 'Please sign these documents');
  const [message, setMessage] = useState('Please review and sign the attached documents at your earliest convenience.');

  // Signing mode: 'email' = send for email signatures, 'inPerson' = in-person signing with host
  const [signingMode, setSigningMode] = useState<'email' | 'inPerson'>('inPerson');
  const [hostEmail, setHostEmail] = useState(employeeEmail || '');
  
  // In-person signing links (shown after successful send)
  const [inPersonSigningLinks, setInPersonSigningLinks] = useState<Array<{
    signerName: string;
    signerRole: string;
    signingUrl: string;
  }> | null>(null);
  const [showSigningLinksDialog, setShowSigningLinksDialog] = useState(false);
  const [successRequestId, setSuccessRequestId] = useState<string | null>(null);
  const [regenerating, setRegenerating] = useState(false);
  const [regenerateError, setRegenerateError] = useState<string | null>(null);

  // Field configuration state
  const [configuringDocument, setConfiguringDocument] = useState<DocumentWithESignFields | null>(null);

  // Load configuration status on mount
  useEffect(() => {
    loadConfigurationStatus();
  }, []);

  const loadConfigurationStatus = async () => {
    setLoading(true);
    setError(null);
    try {
      const bearerToken = await authService.getToken();
      const response = await httpGetAsync('api/zohoSign/configuration', bearerToken!);
      if (response?.ok) {
        const status: ZohoSignConfigurationStatus = await response.json();
        setConfigStatus(status);
        
        // If authenticated, move to document selection
        if (status.isAuthenticated) {
          await loadDocumentsWithESignFields();
          initializeSigners();
          setActiveStep(1);
        }
      } else {
        setError('Failed to load ZohoSign configuration');
      }
    } catch (err) {
      setError('Failed to load ZohoSign configuration');
    } finally {
      setLoading(false);
    }
  };

  const loadDocumentsWithESignFields = async () => {
    try {
      const bearerToken = await authService.getToken();
      const response = await httpPostAsync(
        'api/zohoSign/documents',
        { documentIds },
        bearerToken!
      );
      if (response?.ok) {
        const docs: DocumentWithESignFields[] = await response.json();
        setDocuments(docs);
        // Pre-select all documents by default
        const allDocIds = docs.map(d => d.loanDocumentId);
        setSelectedDocumentIds(allDocIds);
      }
    } catch (err) {
      setError('Failed to load document information');
    }
  };

  const initializeSigners = () => {
    const initialSigners: ESignSignerInput[] = [];
    
    // Add buyer if available
    if (buyerName || buyerEmail) {
      initialSigners.push({
        role: ESignSignerRole.Buyer,
        name: buyerName || '',
        email: buyerEmail || '',
      });
    }
    
    // Add co-buyer if available
    if (coBuyerName || coBuyerEmail) {
      initialSigners.push({
        role: ESignSignerRole.CoBuyer,
        name: coBuyerName || '',
        email: coBuyerEmail || '',
      });
    }
    
    // Always ensure at least buyer signer exists
    if (initialSigners.length === 0) {
      initialSigners.push({
        role: ESignSignerRole.Buyer,
        name: '',
        email: '',
      });
    }
    
    setSigners(initialSigners);
  };

  const handleAuthenticate = async () => {
    try {
      const bearerToken = await authService.getToken();
      const response = await httpGetAsync('api/zohoSign/authUrl', bearerToken!);
      if (response?.ok) {
        const data = await response.json();
        if (data.authUrl) {
          // Open popup window for OAuth flow
          const width = 600;
          const height = 700;
          const left = window.screenX + (window.outerWidth - width) / 2;
          const top = window.screenY + (window.outerHeight - height) / 2;
          
          const popup = window.open(
            data.authUrl,
            'ZohoSignAuth',
            `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`
          );
          
          if (!popup) {
            setError('Popup blocked. Please allow popups for this site.');
            return;
          }
          
          // Poll for popup closure and check for success
          const pollTimer = setInterval(async () => {
            try {
              if (popup.closed) {
                clearInterval(pollTimer);
                // Refresh configuration status to see if auth succeeded
                await loadConfigurationStatus();
              }
            } catch (e) {
              // Cross-origin errors are expected during OAuth redirect
            }
          }, 500);
        }
      }
    } catch (err) {
      setError('Failed to initiate authentication');
    }
  };

  const handleDisconnect = async () => {
    try {
      const bearerToken = await authService.getToken();
      const response = await httpPostAsync('api/zohoSign/disconnect', {}, bearerToken!);
      if (response?.ok) {
        const data = await response.json();
        if (data.success) {
          // Refresh configuration status to show disconnected state
          await loadConfigurationStatus();
        } else {
          setError('Failed to disconnect from ZohoSign');
        }
      } else {
        setError('Failed to disconnect from ZohoSign');
      }
    } catch (err) {
      setError('Failed to disconnect from ZohoSign');
    }
  };

  const handleDocumentToggle = (documentId: string) => {
    setSelectedDocumentIds(prev => {
      if (prev.includes(documentId)) {
        return prev.filter(id => id !== documentId);
      } else {
        return [...prev, documentId];
      }
    });
  };

  const handleSignerChange = (index: number, field: keyof ESignSignerInput, value: any) => {
    setSigners(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const addSigner = (role: ESignSignerRole) => {
    let name = '';
    let email = '';
    
    // Pre-populate employee or dealer with logged-in user
    if ((role === ESignSignerRole.Employee || role === ESignSignerRole.Dealer) && employeeName && employeeEmail) {
      name = employeeName;
      email = employeeEmail;
    }
    
    setSigners(prev => [...prev, { role, name, email }]);
  };

  const removeSigner = (index: number) => {
    setSigners(prev => prev.filter((_, i) => i !== index));
  };

  const canProceedFromStep = (step: number): boolean => {
    switch (step) {
      case 0: // Configure
        return configStatus?.isAuthenticated === true;
      case 1: // Select Documents
        return selectedDocumentIds.length > 0;
      case 2: // Add Signers
        return signers.length > 0 && signers.every(s => s.name && s.email);
      case 3: // Review
        // For in-person signing, host email is required
        if (signingMode === 'inPerson') {
          return hostEmail.trim().length > 0 && hostEmail.includes('@');
        }
        return true;
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (activeStep < steps.length - 1) {
      setActiveStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep(prev => prev - 1);
    }
  };

  const handleSend = async () => {
    setSending(true);
    setError(null);
    
    try {
      const bearerToken = await authService.getToken();
      const response = await httpPostAsync(
        'api/zohoSign/send',
        {
          applicationId,
          documentIds: selectedDocumentIds,
          signers,
          subject,
          message,
          signingMode,
          hostEmail: signingMode === 'inPerson' ? hostEmail : undefined,
        },
        bearerToken!
      );
      
      if (response?.ok) {
        const result = await response.json();
        if (result.success) {
          // For in-person signing, show the signing links dialog
          if (signingMode === 'inPerson' && result.inPersonSigningLinks?.length > 0) {
            setSuccessRequestId(result.requestId);
            setInPersonSigningLinks(result.inPersonSigningLinks);
            setShowSigningLinksDialog(true);
          } else {
            onSuccess?.(result.requestId);
            onClose();
          }
        } else {
          setError(result.errorMessage || 'Failed to send documents');
        }
      } else {
        const errorText = await response?.text();
        setError(errorText || 'Failed to send documents');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to send documents');
    } finally {
      setSending(false);
    }
  };

  const handleCloseSigningLinks = () => {
    setShowSigningLinksDialog(false);
    setInPersonSigningLinks(null);
    if (successRequestId) {
      onSuccess?.(successRequestId);
    }
    onClose();
  };

  const handleCopyLink = (url: string) => {
    navigator.clipboard.writeText(url);
  };

  const handleRegenerateLinks = async () => {
    if (!successRequestId) return;
    
    setRegenerating(true);
    setRegenerateError(null);
    
    try {
      const bearerToken = await authService.getToken();
      const response = await httpPostAsync(
        `api/zohoSign/regenerate-links/${successRequestId}`,
        { hostEmail: hostEmail || undefined },
        bearerToken!
      );
      
      if (response?.ok) {
        const result = await response.json();
        if (result.success && result.inPersonSigningLinks?.length > 0) {
          setInPersonSigningLinks(result.inPersonSigningLinks);
          setRegenerateError(null);
        } else {
          setRegenerateError(result.errorMessage || 'Failed to regenerate links');
        }
      } else {
        const errorText = await response?.text();
        setRegenerateError(errorText || 'Failed to regenerate links');
      }
    } catch (err: any) {
      setRegenerateError(err.message || 'Failed to regenerate links');
    } finally {
      setRegenerating(false);
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return renderConfigurationStep();
      case 1:
        return renderDocumentSelectionStep();
      case 2:
        return renderSignersStep();
      case 3:
        return renderReviewStep();
      default:
        return null;
    }
  };

  const renderConfigurationStep = () => {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
          <CircularProgress />
        </Box>
      );
    }

    if (!configStatus?.isEnabled) {
      return (
        <Notification type={{ style: 'warning', icon: true }}>
          ZohoSign integration is not enabled for your organization. Please contact your administrator.
        </Notification>
      );
    }

    if (!configStatus?.isConfigured) {
      return (
        <Notification type={{ style: 'warning', icon: true }}>
          ZohoSign is not properly configured. Please contact your administrator to set up API credentials.
        </Notification>
      );
    }

    if (!configStatus?.isAuthenticated) {
      return (
        <Box textAlign="center" py={4}>
          <Typography variant="h6" gutterBottom>
            Connect to ZohoSign
          </Typography>
          <Typography color="textSecondary" paragraph>
            You need to authenticate with ZohoSign to send documents for e-signature.
          </Typography>
          <Button variant="contained" color="primary" onClick={handleAuthenticate}>
            Connect to ZohoSign
          </Button>
        </Box>
      );
    }

    return (
      <Notification type={{ style: 'success', icon: true }} style={{ marginBottom: 16, width: '100%' }}>
        <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          <span>You are connected to ZohoSign. Click Next to continue.</span>
          <Button color="inherit" size="small" onClick={handleDisconnect}>
            Disconnect
          </Button>
        </span>
      </Notification>
    );
  };

  const renderDocumentSelectionStep = () => {
    if (documents.length === 0) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
          <CircularProgress />
        </Box>
      );
    }

    const docsWithFields = documents.filter(d => d.hasESignFields);
    const docsWithoutFields = documents.filter(d => !d.hasESignFields);

    const handleConfigureFields = (doc: DocumentWithESignFields, e: React.MouseEvent) => {
      e.stopPropagation();
      setConfiguringDocument(doc);
    };

    return (
      <Box>
        <Typography variant="subtitle1" gutterBottom>
          Select documents to send for e-signature:
        </Typography>
        
        {docsWithFields.length > 0 && (
          <>
            <Typography variant="subtitle2" color="primary" sx={{ mt: 2 }}>
              Documents with signature fields configured:
            </Typography>
            <List>
              {docsWithFields.map((doc) => (
                <ListItem 
                  key={doc.loanDocumentId} 
                  disablePadding
                  secondaryAction={
                    <Tooltip title="Configure signature fields">
                      <IconButton 
                        edge="end" 
                        size="small"
                        onClick={(e) => handleConfigureFields(doc, e)}
                      >
                        <SettingsIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  }
                >
                  <ListItemButton onClick={() => handleDocumentToggle(doc.loanDocumentId)} dense>
                    <ListItemIcon>
                      <Checkbox
                        edge="start"
                        checked={selectedDocumentIds.includes(doc.loanDocumentId)}
                        tabIndex={-1}
                      />
                    </ListItemIcon>
                    <ListItemText 
                      primary={doc.name}
                      secondary={`${doc.fieldCount ?? 0} signature field(s)`}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </>
        )}

        {docsWithoutFields.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" color="textSecondary">
              Documents without signature fields (click ⚙ to configure):
            </Typography>
            <List>
              {docsWithoutFields.map((doc) => (
                <ListItem 
                  key={doc.loanDocumentId} 
                  disablePadding
                  secondaryAction={
                    <Tooltip title="Configure signature fields">
                      <IconButton 
                        edge="end" 
                        size="small"
                        onClick={(e) => handleConfigureFields(doc, e)}
                      >
                        <SettingsIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  }
                >
                  <ListItemButton onClick={() => handleDocumentToggle(doc.loanDocumentId)} dense>
                    <ListItemIcon>
                      <Checkbox
                        edge="start"
                        checked={selectedDocumentIds.includes(doc.loanDocumentId)}
                        tabIndex={-1}
                      />
                    </ListItemIcon>
                    <ListItemText primary={doc.name} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </>
        )}

        {selectedDocumentIds.length === 0 && (
          <Notification type={{ style: 'info', icon: true }} style={{ marginTop: 16 }}>
            Please select at least one document to send for e-signature.
          </Notification>
        )}
      </Box>
    );
  };

  const renderSignersStep = () => {
    return (
      <Box>
        <Typography variant="subtitle1" gutterBottom>
          Add signers for this document package:
        </Typography>

        {signers.map((signer, index) => (
          <Box key={index} sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={3}>
                <Typography variant="subtitle2">
                  {ESignSignerRoleOptions.find(o => o.value === signer.role)?.label || 'Signer'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Name"
                  value={signer.name}
                  onChange={(e) => handleSignerChange(index, 'name', e.target.value)}
                  fullWidth
                  size="small"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Email"
                  type="email"
                  value={signer.email}
                  onChange={(e) => handleSignerChange(index, 'email', e.target.value)}
                  fullWidth
                  size="small"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={1}>
                {signers.length > 1 && (
                  <Button
                    color="error"
                    size="small"
                    onClick={() => removeSigner(index)}
                  >
                    Remove
                  </Button>
                )}
              </Grid>
            </Grid>
          </Box>
        ))}

        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Add additional signers:
          </Typography>
          <Box display="flex" gap={1}>
            {!signers.some(s => s.role === ESignSignerRole.Buyer) && (
              <Button size="small" onClick={() => addSigner(ESignSignerRole.Buyer)}>
                + Buyer
              </Button>
            )}
            {!signers.some(s => s.role === ESignSignerRole.CoBuyer) && (
              <Button size="small" onClick={() => addSigner(ESignSignerRole.CoBuyer)}>
                + Co-Buyer
              </Button>
            )}
            {!signers.some(s => s.role === ESignSignerRole.CoBuyer2) && (
              <Button size="small" onClick={() => addSigner(ESignSignerRole.CoBuyer2)}>
                + Co-Buyer 2
              </Button>
            )}
            {!signers.some(s => s.role === ESignSignerRole.Dealer) && (
              <Button size="small" onClick={() => addSigner(ESignSignerRole.Dealer)}>
                + Dealer
              </Button>
            )}
            {!signers.some(s => s.role === ESignSignerRole.Employee) && (
              <Button size="small" onClick={() => addSigner(ESignSignerRole.Employee)}>
                + Employee
              </Button>
            )}
            {!signers.some(s => s.role === ESignSignerRole.Lender) && (
              <Button size="small" onClick={() => addSigner(ESignSignerRole.Lender)}>
                + Lender
              </Button>
            )}
            {!signers.some(s => s.role === ESignSignerRole.ThirdPartyOwner) && (
              <Button size="small" onClick={() => addSigner(ESignSignerRole.ThirdPartyOwner)}>
                + Third Party Owner
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    );
  };

  const renderReviewStep = () => {
    const selectedDocs = documents.filter(d => selectedDocumentIds.includes(d.loanDocumentId));

    return (
      <Box>
        <Typography variant="subtitle1" gutterBottom>
          Review and customize your e-signature request:
        </Typography>

        {/* Signing Mode Selection */}
        <FormControl component="fieldset" sx={{ mb: 3 }}>
          <FormLabel component="legend">Signing Method</FormLabel>
          <RadioGroup
            row
            value={signingMode}
            onChange={(e) => setSigningMode(e.target.value as 'email' | 'inPerson')}
          >
            <FormControlLabel 
              value="email" 
              control={<Radio />} 
              label="Send via Email"
            />
            <FormControlLabel 
              value="inPerson" 
              control={<Radio />} 
              label="In-Person Signing"
            />
          </RadioGroup>
        </FormControl>

        {signingMode === 'inPerson' && (
          <Notification type={{ style: 'info', icon: true }} style={{ marginBottom: 16 }}>
            Signing links will be emailed to the host. The host can open each link on any device (e.g., iPad) for signers to sign in person.
          </Notification>
        )}

        <Box sx={{ mb: 3 }}>
          {signingMode === 'inPerson' && (
            <TextField
              label="Host Email (receives signing links)"
              value={hostEmail}
              onChange={(e) => setHostEmail(e.target.value)}
              fullWidth
              sx={{ mb: 2 }}
              type="email"
              required
              helperText="The host will receive an email with links for each signer to sign in person"
            />
          )}
          <TextField
            label="Email Subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
          />
          <TextField
            label="Message to Signers"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            fullWidth
            multiline
            rows={3}
          />
        </Box>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" gutterBottom>
          Documents ({selectedDocs.length}):
        </Typography>
        <List dense>
          {selectedDocs.map(doc => (
            <ListItem key={doc.loanDocumentId}>
              <ListItemText primary={doc.name} />
            </ListItem>
          ))}
        </List>

        <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
          Signers ({signers.length}):
        </Typography>
        <List dense>
          {signers.map((signer, index) => (
            <ListItem key={index}>
              <ListItemText 
                primary={signer.name}
                secondary={`${ESignSignerRoleOptions.find(o => o.value === signer.role)?.label} - ${signer.email}`}
              />
            </ListItem>
          ))}
        </List>
      </Box>
    );
  };

  return (
    <>
    <Dialog open={true} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Send for E-Signature</DialogTitle>
      <DialogContent>
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Notification type={{ style: 'error', icon: true }} style={{ marginBottom: 16 }}>
            {error}
          </Notification>
        )}

        {renderStepContent()}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={sending}>
          Cancel
        </Button>
        {activeStep > 0 && (
          <Button onClick={handleBack} disabled={sending}>
            Back
          </Button>
        )}
        {activeStep < steps.length - 1 ? (
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={!canProceedFromStep(activeStep)}
          >
            Next
          </Button>
        ) : (
          <Button
            variant="contained"
            color="primary"
            onClick={handleSend}
            disabled={sending || !canProceedFromStep(activeStep)}
          >
            {sending ? <CircularProgress size={20} /> : 'Send for Signature'}
          </Button>
        )}
      </DialogActions>
    </Dialog>

    {/* PDF Coordinate Picker for configuring signature fields */}
    {configuringDocument && (
      <PdfCoordinatePicker
        loanDocumentId={configuringDocument.loanDocumentId}
        documentName={configuringDocument.name}
        onClose={() => setConfiguringDocument(null)}
        onSaved={() => {
          // Refresh document list to update field counts
          loadDocumentsWithESignFields();
          setConfiguringDocument(null);
        }}
      />
    )}

    {/* In-Person Signing Links Dialog */}
    <Dialog 
      open={showSigningLinksDialog} 
      onClose={handleCloseSigningLinks}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <LinkIcon color="primary" />
          In-Person Signing Links
        </Box>
      </DialogTitle>
      <DialogContent>
        <Notification type={{ style: 'success', icon: true }} style={{ marginBottom: 16 }}>
          Documents have been submitted for signing. Use the links below to have signers sign in person.
        </Notification>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Open each link on a device (tablet, phone, or computer) and have the signer complete their signature.
          Links are valid for a limited time.
        </Typography>

        <TextField
          fullWidth
          label="Host Email (for resending links)"
          value={hostEmail}
          onChange={(e) => setHostEmail(e.target.value)}
          size="small"
          sx={{ mb: 2 }}
          helperText="Click 'Regenerate Links' to get new links and email them to this address"
        />
        
        {inPersonSigningLinks?.map((link, index) => (
          <Paper key={index} sx={{ p: 2, mb: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="subtitle1" fontWeight="bold">
                  {link.signerName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {link.signerRole}
                </Typography>
              </Box>
              <Box display="flex" gap={1}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => handleCopyLink(link.signingUrl)}
                  startIcon={<ContentCopyIcon />}
                >
                  Copy Link
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  href={link.signingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  startIcon={<OpenInNewIcon />}
                >
                  Open
                </Button>
              </Box>
            </Box>
          </Paper>
        ))}
        
        {regenerateError && (
          <Notification type={{ style: 'error', icon: true }} style={{ marginTop: 16 }}>
            {regenerateError}
          </Notification>
        )}
      </DialogContent>
      <DialogActions>
        <Button 
          onClick={handleRegenerateLinks} 
          disabled={regenerating}
          startIcon={regenerating ? <CircularProgress size={16} /> : <RefreshIcon />}
        >
          {regenerating 
            ? 'Regenerating...' 
            : hostEmail 
              ? 'Regenerate & Email Links' 
              : 'Regenerate Links'}
        </Button>
        <Button onClick={handleCloseSigningLinks} variant="contained">
          Done
        </Button>
      </DialogActions>
    </Dialog>
  </>
  );
}
